import React from 'react';

import {StudyPhase, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {ProcedureWithAnalysisTemplates} from 'procedures/shared-components/analysis/PreconfiguredAnalysisMissingTemplatesWarningBanner';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import {CLINICAL_STUDIES_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import {ClinicalBasedStudyExecutionOverviewQuery_study_StudyNode_activeProcedureRuns as ProcedureRunNode} from 'studies/typed/clinical/__generated/ClinicalBasedStudyExecutionOverviewQuery';
import AddAssayRunsModal from 'studies/typed/clinical/AddAssayRunsModal';
import {getProcedureTypeForStudyTypeAllowNull} from 'studies/TypedStudyUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import runModal from 'ui-library/runModal';

export const EXPERIMENTS_NOT_ALLOWED = 'Assay runs can not be added for this study.';
export const REOPEN_STUDY_TOOLTIP = 'Reopen your study to add an assay run';
export const NO_TEMPLATES_AVAILABLE = 'No assays are available';
export const MOVE_TO_EXECUTION_STUDY_TOOLTIP = 'Move your study to execution to add an assay run';
export const MAX_NUM_ASSAY_RUNS_TOOLTIP = 'Maximum number of assay runs reached';

export interface StudyForAddAssayRunsButton {
  id: string;
  studySchema: {
    studyType: StudyType;
  };
  phase: StudyPhase;
  procedureRuns: Array<ProcedureRunNode>;
  allowedProcedureTemplates: Array<ProcedureWithAnalysisTemplates>;
}

function isButtonDisabled(study: StudyForAddAssayRunsButton): {disabled: boolean; tooltipText?: string} {
  const buttonStatus = {disabled: false, tooltipText: ''};

  const procedureType = getProcedureTypeForStudyTypeAllowNull(study.studySchema.studyType);
  if (procedureType == null) {
    buttonStatus.disabled = true;
    buttonStatus.tooltipText = EXPERIMENTS_NOT_ALLOWED;
  } else if (!isFeatureEnabledForProcedureType(procedureType, ProceduresFeature.PLAN_EXPERIMENT)) {
    const procedureTypeName = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
      stringCase: StringCase.SENTENCE,
      plural: true,
    });
    buttonStatus.disabled = true;
    buttonStatus.tooltipText = `You do not have sufficient permissions to use ${procedureTypeName}. Please contact your administrator.`;
  } else if (study.phase === StudyPhase.DESIGN) {
    buttonStatus.disabled = true;
    buttonStatus.tooltipText = MOVE_TO_EXECUTION_STUDY_TOOLTIP;
  } else if (study.phase !== StudyPhase.EXECUTION) {
    buttonStatus.disabled = true;
    buttonStatus.tooltipText = REOPEN_STUDY_TOOLTIP;
  } else if (study.allowedProcedureTemplates.length === 0) {
    buttonStatus.disabled = true;
    buttonStatus.tooltipText = NO_TEMPLATES_AVAILABLE;
  } else if (
    study.procedureRuns.length >=
    CLINICAL_STUDIES_VALIDATION_CONSTANTS.MAX_NUM_ASSAY_RUNS_PER_CLINICAL_BASED_STUDY
  ) {
    buttonStatus.disabled = true;
    buttonStatus.tooltipText = MAX_NUM_ASSAY_RUNS_TOOLTIP;
  }
  return buttonStatus;
}

/**
 * This component should only be rendered for study types that support multiple assay runs.
 */
export default function AddAssayRunsButton({
  study,
  buttonSize,
}: {
  study: StudyForAddAssayRunsButton;
  buttonSize?: ButtonSize;
}): JSX.Element {
  const {disabled, tooltipText} = isButtonDisabled(study);
  return (
    <Tooltip isDisabled={!disabled} text={tooltipText}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        size={buttonSize ?? ButtonSize.MINI}
        fullWidth={false}
        icon="plus"
        text="Add run"
        onClick={async () => {
          await runModal(AddAssayRunsModal, {
            studyType: study.studySchema.studyType,
            studyId: study.id,
            procedureTemplatesToRender: study.allowedProcedureTemplates,
          });
        }}
        disabled={disabled}
        dataTestAttrs={{component: 'AddAssayRunsModal', element: 'addAssayRunsButton'}}
      />
    </Tooltip>
  );
}
